import {useState, useEffect} from "react";
import {Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, IconButton, Stack, TextField, Tooltip, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {Navigate, useParams, useNavigate} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import {StyledSearch, StyledSearchIconWrapper, StyledInputBase} from "../../AppStyledComponents.tsx";
import type {DiscussionTopic} from "../../../sdo/DiscussionTopic.ts";

// TODO: Remove once login flow is wired and appSession.getUsername() returns a real value in dev.
const DEV_USER = "dev-user";

// TODO: Replace with API call to load discussion topics
function buildTestData(currentUser: string): DiscussionTopic[] {
    return [
        {uid: "topic-1", createdAt: new Date(2026, 2, 15, 10, 0).getTime(), createdBy: currentUser, title: "Operational Security Protocols", description: "Discussion around updated OPSEC guidelines for field teams and remote analysts."},
        {uid: "topic-2", createdAt: new Date(2026, 3, 1, 14, 30).getTime(), createdBy: currentUser, title: "", description: "Reviewing patterns identified in Q1 signal intercepts. Collaboration needed from SIGINT and HUMINT teams."},
        {uid: "topic-3", createdAt: new Date(2026, 3, 5, 9, 15).getTime(), createdBy: "K. Andersen", title: "Logistics Coordination - Eastern Sector", description: "Supply chain and transportation routes under review. Input needed from regional leads."},
        {uid: "topic-4", createdAt: new Date(2026, 3, 10, 16, 45).getTime(), createdBy: "M. Chen", title: "", description: "Evaluating new OSINT tools and data feeds for integration into the platform."},
        {uid: "topic-5", createdAt: new Date(2026, 3, 11, 8, 0).getTime(), createdBy: "A. Petrov", title: "Threat Assessment - Maritime Corridor", description: "Ongoing threat analysis for the southern maritime corridor. Multi-agency collaboration thread."},
    ];
}

// Client-side post counts — replaces the former postCount field on DiscussionTopic.
// TODO: Replace with API call (e.g. discussionManager.countPosts(topicUID)) per topic.
const SAMPLE_POST_COUNTS: Record<string, number> = {
    "topic-1": 7,
    "topic-2": 0,
    "topic-3": 3,
    "topic-4": 0,
    "topic-5": 4,
};

const topicCardSx = {
    background: "linear-gradient(135deg, rgba(9,25,32,0.85) 0%, rgba(20,40,55,0.7) 100%)",
    border: "1px solid #3F464F",
    borderRadius: "12px",
    padding: "12px 16px",
    width: 360,
    minWidth: 300,
    cursor: "pointer",
    "&:hover": {
        borderColor: "rgba(2, 207, 238, 0.45)",
        backgroundColor: "rgba(2, 207, 238, 0.04)",
    },
};

const topicTitleSx = {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#F2F5FA",
    textAlign: "left" as const,
};

const topicDescSx = {
    fontSize: "0.7rem",
    fontWeight: 300,
    color: "rgba(242, 245, 250, 0.7)",
    textAlign: "left" as const,
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
};

const topicDateSx = {
    fontSize: "0.6rem",
    fontWeight: 400,
    color: "#a8b8c0",
    textAlign: "left" as const,
};

const formFieldSx = {
    backgroundColor: "rgba(9, 25, 32, 0.6)",
    borderRadius: "6px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        color: "#f2f5fa",
        fontSize: "0.8rem",
        "& fieldset": {border: "1px solid rgba(112, 147, 160, 0.35)"},
        "&:hover fieldset": {border: "1px solid #02cfee"},
        "&.Mui-focused fieldset": {border: "1px solid #38ff82", boxShadow: "0 0 6px rgba(56, 255, 130, 0.2)"},
    },
    "& .MuiInputLabel-root": {color: "rgba(242, 245, 250, 0.55)", fontSize: "0.8rem"},
    "& .MuiInputLabel-root.Mui-focused": {color: "#38ff82"},
};

function formatDate(epoch: number): string {
    const d = new Date(epoch);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function TopicCard({topic, onClick, onEdit}: { topic: DiscussionTopic; onClick: () => void; onEdit?: () => void }) {
    const [hovered, setHovered] = useState(false);
    const hasTitle = topic.title.trim().length > 0;

    return (
        <Box
            sx={{...topicCardSx, position: "relative"}}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {onEdit && (
                <Tooltip title="Edit topic">
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                        sx={{
                            position: "absolute", top: 6, right: 6,
                            opacity: hovered ? 1 : 0, transition: "opacity 0.15s",
                            padding: "4px", color: "#7093a0",
                            "&:hover": {color: "#02cfee"},
                        }}
                    >
                        <EditIcon sx={{fontSize: 16}} />
                    </IconButton>
                </Tooltip>
            )}

            <Stack spacing={0.3}>
                {hasTitle && <Typography sx={topicTitleSx}>{topic.title}</Typography>}
                <Typography sx={topicDescSx}>{topic.description}</Typography>
                <Typography sx={topicDateSx}>{formatDate(topic.createdAt)} · by {topic.createdBy}</Typography>
            </Stack>
        </Box>
    );
}

interface TopicFormProps {
    initial?: DiscussionTopic;
    currentUser: string;
    onSave: (topic: DiscussionTopic) => void;
    onCancel: () => void;
    onDelete?: () => void;
    canDelete?: boolean;
}

function TopicForm({initial, currentUser, onSave, onCancel, onDelete, canDelete}: TopicFormProps) {
    const isEdit = !!initial;
    const [title, setTitle] = useState(initial?.title ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");
    const [confirmDelete, setConfirmDelete] = useState(false);

    const descTrimmed = description.trim();
    const saveDisabled = descTrimmed.length === 0;

    function handleSave() {
        if (saveDisabled) return;
        if (isEdit && initial) {
            onSave({
                ...initial,
                title: title.trim(),
                description: descTrimmed,
            });
        } else {
            onSave({
                uid: `topic-new-${Date.now()}`,
                createdAt: Date.now(),
                createdBy: currentUser,
                title: title.trim(),
                description: descTrimmed,
            });
        }
    }

    return (
        <Box sx={{
            width: "100%",
            maxWidth: 600,
            background: "linear-gradient(135deg, rgba(9,25,32,0.9) 0%, rgba(20,40,55,0.75) 100%)",
            border: "1px solid rgba(2, 207, 238, 0.35)",
            borderRadius: "12px",
            padding: "16px 20px",
        }}>
            <Stack spacing={1.5}>
                <TextField
                    size="small"
                    label="Title (optional)"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    sx={formFieldSx}
                    fullWidth
                />
                <TextField
                    size="small"
                    label="Description"
                    required
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    multiline
                    minRows={2}
                    maxRows={4}
                    sx={{...formFieldSx, "& .MuiOutlinedInput-root": {...formFieldSx["& .MuiOutlinedInput-root"], height: "auto"}}}
                    fullWidth
                />
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Box>
                        {isEdit && onDelete && (
                            <Tooltip title={canDelete ? "Delete topic" : "Topics with posts cannot be deleted"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => setConfirmDelete(true)}
                                        disabled={!canDelete}
                                        sx={{
                                            padding: "4px",
                                            color: "#7093a0",
                                            "&:hover": {color: "#d83f2f"},
                                            "&.Mui-disabled": {color: "rgba(112, 147, 160, 0.35)"},
                                        }}
                                    >
                                        <DeleteIcon sx={{fontSize: 18}} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onCancel}
                            startIcon={<CloseIcon sx={{fontSize: 14}} />}
                            sx={{
                                fontSize: "0.7rem", textTransform: "none",
                                borderColor: "#7093a0", color: "#a8b8c0",
                                "&:hover": {borderColor: "#d06440", color: "#d06440"},
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSave}
                            disabled={saveDisabled}
                            sx={{
                                ...appStyles.panelActionButton,
                                "&.Mui-disabled": {
                                    backgroundColor: "rgba(2, 207, 238, 0.15)",
                                    color: "rgba(2, 207, 238, 0.4)",
                                },
                            }}
                        >
                            {isEdit ? "Save Changes" : "Create Topic"}
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                slotProps={{paper: {sx: {backgroundColor: "#1a2730", border: "1px solid #3F464F", borderRadius: "12px"}}}}
            >
                <DialogContent>
                    <DialogContentText sx={{color: "#f2f5fa", fontSize: "0.90rem"}}>
                        Are you sure you want to delete this topic?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{padding: "8px 16px 12px"}}>
                    <Button onClick={() => setConfirmDelete(false)} sx={{color: "#7093a0", fontSize: "0.80rem"}}>Cancel</Button>
                    <Button onClick={() => { setConfirmDelete(false); onDelete?.(); }} sx={{color: "#d83f2f", fontSize: "0.80rem"}}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function UXDiscussionSelect() {
    const {networkUID} = useParams();
    const navigate = useNavigate();
    const [topics, setTopics] = useState<DiscussionTopic[]>([]);
    const [filterText, setFilterText] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [editingUID, setEditingUID] = useState<string | null>(null);

    // TODO: Drop DEV_USER fallback once appSession.getUsername() returns a real value in dev.
    const currentUser = appSession.getUsername() ?? DEV_USER;

    useEffect(() => {
        // TODO: Replace with API call
        setTopics(buildTestData(currentUser));
    }, [networkUID, currentUser]);

    const filtered = topics.filter(t => {
        const q = filterText.trim().toLowerCase();
        if (!q) return true;
        return t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
    });

    function handleCreateTopic(topic: DiscussionTopic) {
        // TODO: Replace with API call to save topic
        setTopics(prev => [topic, ...prev]);
        setShowCreate(false);
    }

    function handleUpdateTopic(topic: DiscussionTopic) {
        // TODO: Replace with API call to update topic (createdAt/createdBy unchanged)
        setTopics(prev => prev.map(t => t.uid === topic.uid ? topic : t));
        setEditingUID(null);
    }

    function handleDeleteTopic(uid: string) {
        // TODO: Replace with API call to delete topic
        setTopics(prev => prev.filter(t => t.uid !== uid));
        setEditingUID(null);
    }

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Discussions"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Discussions", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" alignItems="center">
                        <Typography sx={{...appStyles.panelSectionTitle, padding: "16px 0 8px"}}>
                            Discussions
                        </Typography>

                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{padding: "0 16px 12px"}} flexWrap="wrap">
                            <StyledSearch>
                                <StyledSearchIconWrapper>
                                    <img src="/images/search_white.svg" width={16} />
                                </StyledSearchIconWrapper>
                                <StyledInputBase
                                    placeholder="Filter"
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    inputProps={{"aria-label": "filter"}}
                                />
                            </StyledSearch>
                            <Typography sx={appStyles.showCountText}>
                                Showing {filtered.length} of {topics.length}
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setShowCreate(prev => !prev)}
                                sx={appStyles.panelActionButton}
                            >
                                New Topic
                            </Button>
                        </Stack>

                        <Collapse in={showCreate} sx={{width: "100%"}}>
                            <Box sx={{display: "flex", justifyContent: "center", padding: "0 16px 16px"}}>
                                <TopicForm
                                    currentUser={currentUser}
                                    onSave={handleCreateTopic}
                                    onCancel={() => setShowCreate(false)}
                                />
                            </Box>
                        </Collapse>

                        {filtered.length > 0 ? (
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center", padding: "8px 16px 16px"}}>
                                {filtered.map(topic => {
                                    const username = appSession.getUsername() ?? DEV_USER;
                                    const canEdit = username !== null && topic.createdBy === username;
                                    const postCount = SAMPLE_POST_COUNTS[topic.uid] ?? 0;
                                    if (editingUID === topic.uid) {
                                        return (
                                            <Box key={topic.uid} sx={{width: "100%", display: "flex", justifyContent: "center"}}>
                                                <TopicForm
                                                    initial={topic}
                                                    currentUser={currentUser}
                                                    onSave={handleUpdateTopic}
                                                    onCancel={() => setEditingUID(null)}
                                                    onDelete={() => handleDeleteTopic(topic.uid)}
                                                    canDelete={postCount === 0}
                                                />
                                            </Box>
                                        );
                                    }
                                    return (
                                        <TopicCard
                                            key={topic.uid}
                                            topic={topic}
                                            onClick={() => navigate(`/networks/${networkUID}/discussions/${topic.uid}`)}
                                            onEdit={canEdit ? () => setEditingUID(topic.uid) : undefined}
                                        />
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography sx={{color: "#a8b8c0", fontSize: "0.85rem", padding: "32px 0"}}>
                                No discussions found.
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXDiscussionSelect;
